import classNames from 'classnames';

import { useAccess, useHasAccess } from '../../hooks/useAccess';
import { useCollectionSurtitle } from '../../hooks/useSurtitle';
import { useCollectionUrlGenerator } from '../../hooks/useUrlGenerator';
// ...existing code...
import getRatio from '../../lib/getRatio';
import normalizeCardTheme from '../../lib/normalizeCardTheme';

import CollectionImage from '../partials/CollectionImage';
import Sponsor from '../partials/Sponsor';
import Surtitle from '../partials/Surtitle';
import CardDescription from '../typography/CardDescription';
import CardTitle from '../typography/CardTitle';
import VerticalCard from './VerticalCard';

import styles from '../../styles/cards/collection-vertical-card.module.css';

export interface CollectionVerticalCardProps extends Partial<Omit<CollectionSnippet, 'theme'>> {
    image?: Image | null;
    theme?: CardTheme | LegacyCardTheme | null;
    size?: CardSize | null;
    ratio?: CardRatio | null;
    width?: number;
    withBorder?: boolean;
    withDescription?: boolean;
    withoutSurtitle?: boolean;
    withBrandIcon?: boolean;
    withoutCategory?: boolean;
    withoutSerieFormat?: boolean;
    withBroadcast?: boolean;
    placeholder?: boolean;
    className?: string | null;
}

function CollectionVerticalCard({
    type = null,
    slug = null,
    surtitle = null,
    title = null,
    description = null,
    image = null,
    brands = null,
    categories = null,
    format = null,
    topic = null,
    topics = null,
    formats = null,
    sponsors = null,
    access = null,
    serie = null,
    broadcasts = null,
    itemsCount = null,
    childrenCount = null,
    issueNumber = null,
    serieFormat = null,
    ratio = null,
    theme: providedTheme = null,
    size = null,
    width = 240,
    withBorder = false,
    withDescription = false,
    withoutSurtitle = false,
    withBrandIcon = false,
    withoutSerieFormat = false,
    withoutCategory = false,
    withBroadcast = false,
    placeholder = false,
    className = null,
}: CollectionVerticalCardProps) {
    const theme = normalizeCardTheme(providedTheme);
    const collectionUrl = useCollectionUrlGenerator();
    const [brand = null] = brands || [];
    const { handle: brandHandle } = brand || {};
    const [sponsor = null] = sponsors || [];
    const [broadcast = null] = broadcasts || [];
    const finalAccess = useAccess({
        serie,
        access,
    });
    const { blocked = false } = finalAccess || {};
    const unblocked = useHasAccess(finalAccess);

    const thumbnailRatio = getRatio(ratio, 1);

    const finalSurtitle = useCollectionSurtitle({
        type,
        surtitle,
        itemsCount,
        childrenCount,
        issueNumber,
        serieFormat,
        categories,
        topics: topics || (topic !== null ? [topic] : null),
        formats: formats || (format !== null ? [format] : null),
        withSerieFormat: !withoutSerieFormat,
        withCategory: !withoutCategory,
    });

    return (
        <VerticalCard
            href={
                collectionUrl({
                    type,
                    slug,
                }) || '#'
            }
            thumbnail={
                <CollectionImage
                    brand={brandHandle}
                    title={title}
                    width={width}
                    height={width / thumbnailRatio}
                    media={image}
                    broadcast={broadcast}
                    blocked={blocked}
                    unblocked={unblocked}
                    withBroadcast={withBroadcast}
                    withBrandIcon={withBrandIcon}
                    withPlaceholder={placeholder}
                    className={styles.thumbnail}
                    withoutSize
                />
            }
            thumbnailRatio={thumbnailRatio}
            className={classNames([
                styles.container,
                {
                    [styles.withBorder]: withBorder,
                },
                className,
            ])}
            presentation="collection-vertical"
            theme={theme || 'none'}
            size={size || 'default'}
            labelClassName={styles.label}
            innerClassName={styles.inner}
            thumbnailClassName={styles.thumbnailContainer}
        >
            {!withoutSurtitle ? (
                <Surtitle surtitle={finalSurtitle} className={styles.surtitle} />
            ) : null}
            <CardTitle className={styles.title} placeholder={placeholder}>
                {title}
            </CardTitle>
            {withDescription ? (
                <CardDescription
                    className={styles.description}
                    html={description}
                    placeholder={placeholder}
                />
            ) : null}

            {sponsor !== null ? (
                <div className={styles.meta}>
                    <Sponsor
                        {...sponsor}
                        className={styles.sponsor}
                        labelClassName={styles.sponsorLabel}
                    />
                </div>
            ) : null}
        </VerticalCard>
    );
}

export default CollectionVerticalCard;
